import React, { useEffect, useRef } from 'react';

/* ------------------------------------------------------------------ */
/*  VoiceWaveform — the live waveform of a voice note: one bar per    */
/*  slice of time, the newest on the right, the older ones sliding    */
/*  left; a dotted line holds the place of what is not yet recorded.  */
/*  Drawn on a canvas in the text colour (CSS `color`).               */
/* ------------------------------------------------------------------ */

const BAR = 3; // px
const GAP = 3; // px
const STEP_MS = 70; // one bar per 70 ms: about 14 bars a second
const MIN_BAR = 0.08; // silence still shows as a small tick, like the dots

/** Speech sits around 0.02-0.2 RMS: stretched so a normal voice fills the bar. */
const toHeight = (level: number) => Math.max(MIN_BAR, Math.min(1, Math.pow(level * 6, 0.7)));

interface Props {
  /** Current loudness, 0-1 (VoiceRecorder.level). */
  level: () => number;
  /** False once recording stopped: the waveform stays, frozen. */
  live: boolean;
}

const VoiceWaveform: React.FC<Props> = ({ level, live }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bars = useRef<number[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frame = 0;
    let last = 0;
    let peak = 0;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = getComputedStyle(canvas).color;

      const step = BAR + GAP;
      const slots = Math.floor(width / step);
      const shown = bars.current.slice(-slots);
      const mid = height / 2;

      // Not yet recorded: dots, on the left
      ctx.globalAlpha = 0.35;
      for (let i = 0; i < slots - shown.length; i++) {
        ctx.beginPath();
        ctx.arc(i * step + BAR / 2, mid, 1, 0, Math.PI * 2);
        ctx.fill();
      }
      // Recorded: bars, the newest against the right edge
      ctx.globalAlpha = 1;
      const offset = slots - shown.length;
      shown.forEach((h, i) => {
        const barHeight = Math.max(2, h * height);
        const x = (offset + i) * step;
        if (typeof ctx.roundRect === 'function') {
          ctx.beginPath();
          ctx.roundRect(x, mid - barHeight / 2, BAR, barHeight, BAR / 2);
          ctx.fill();
        } else {
          ctx.fillRect(x, mid - barHeight / 2, BAR, barHeight); // Safari < 16, Firefox < 112
        }
      });
    };

    const tick = (now: number) => {
      peak = Math.max(peak, level());
      if (now - last >= STEP_MS) {
        bars.current.push(toHeight(peak));
        if (bars.current.length > 2000) bars.current.splice(0, 1000);
        peak = 0;
        last = now;
      }
      draw();
      frame = requestAnimationFrame(tick);
    };

    if (live) frame = requestAnimationFrame(tick);
    else draw();
    return () => cancelAnimationFrame(frame);
  }, [level, live]);

  return <canvas ref={canvasRef} className="voice-waveform" aria-hidden="true" />;
};

export default VoiceWaveform;
